import { useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { Play, Pause, RotateCcw, StopCircle } from 'lucide-react'
import { api } from '@/lib/api'
import { useTaskStore } from '@/store/useTaskStore'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { StatusBadge } from './StatusBadge'

export function RunControls() {
  const queryClient = useQueryClient()
  const { selectedTaskId, tasks, updateTaskStatus, setActiveRunId } = useTaskStore()
  const task = tasks.find((t) => t.id === selectedTaskId)

  const runMutation = useMutation({
    mutationFn: () => api.runTask(selectedTaskId!),
    onSuccess: (data) => {
      updateTaskStatus(selectedTaskId!, 'running')
      setActiveRunId(data.run_id)
      queryClient.invalidateQueries({ queryKey: ['tasks'] })
      toast.success('Pipeline started')
    },
    onError: (error: Error) => {
      toast.error(`Failed to start pipeline: ${error.message}`)
    },
  })

  const pauseMutation = useMutation({
    mutationFn: () => api.pauseTask(selectedTaskId!),
    onSuccess: () => {
      updateTaskStatus(selectedTaskId!, 'paused')
      toast.success('Pipeline paused')
    },
    onError: (error: Error) => {
      toast.error(`Failed to pause pipeline: ${error.message}`)
    },
  })

  const resumeMutation = useMutation({
    mutationFn: () => api.resumeTask(selectedTaskId!),
    onSuccess: () => {
      updateTaskStatus(selectedTaskId!, 'running')
      toast.success('Pipeline resumed')
    },
    onError: (error: Error) => {
      toast.error(`Failed to resume pipeline: ${error.message}`)
    },
  })

  const stopMutation = useMutation({
    mutationFn: () => api.stopTask(selectedTaskId!),
    onSuccess: () => {
      updateTaskStatus(selectedTaskId!, 'stopped')
      toast.success('Pipeline stopped')
    },
    onError: (error: Error) => {
      toast.error(`Failed to stop pipeline: ${error.message}`)
    },
  })

  if (!task) {
    return (
      <Card>
        <CardContent className="p-6">
          <p className="text-sm text-slate-500 text-center">
            Select a task to view run controls
          </p>
        </CardContent>
      </Card>
    )
  }

  const canRun = task.status === 'idle' || task.status === 'complete' || task.status === 'error' || task.status === 'stopped'
  const canPause = task.status === 'running'
  const canResume = task.status === 'paused'
  const canStop = task.status === 'running' || task.status === 'paused'

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle>Run Control</CardTitle>
          <StatusBadge status={task.status} />
        </div>
      </CardHeader>
      <CardContent>
        <div className="flex flex-wrap gap-2">
          <Button
            onClick={() => runMutation.mutate()}
            disabled={!canRun || runMutation.isPending}
          >
            <Play className="w-4 h-4 mr-2" />
            Run Task
          </Button>

          <Button
            onClick={() => pauseMutation.mutate()}
            disabled={!canPause || pauseMutation.isPending}
            variant="outline"
          >
            <Pause className="w-4 h-4 mr-2" />
            Pause
          </Button>

          <Button
            onClick={() => resumeMutation.mutate()}
            disabled={!canResume || resumeMutation.isPending}
          >
            <RotateCcw className="w-4 h-4 mr-2" />
            Resume
          </Button>

          <Button
            onClick={() => stopMutation.mutate()}
            disabled={!canStop || stopMutation.isPending}
            variant="destructive"
          >
            <StopCircle className="w-4 h-4 mr-2" />
            Stop
          </Button>
        </div>
      </CardContent>
    </Card>
  )
}
